import { useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import LogRocket from 'logrocket';
import API from 'services/API';
import { getCurrentUser } from 'services/Authentication';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { PERSISTENCE_KEYS } from 'UI/constants/persistenceKeys';
import { UIStatus } from 'UI/constants/status';

import { useFeatureFlags } from './FeatureFlagsProvider';

const USER_DATA_TIMEOUT_MS = 8000;

const getLogRocketDomains = () => {
  const domainsSetting = `${
    window.GPAC_ENV?.LOG_ROCKET_DOMAINS || process.env.REACT_APP_LOG_ROCKET_DOMAINS
  }`;
  const domains = domainsSetting ? domainsSetting.split('|') : [];
  return {
    rootHostname: domains.shift(),
    childDomains: domains
  };
};

export const useLogRocket = ({ user }) => {
  useEffect(() => {
    const logRocketKey = window.GPAC_ENV?.LOG_ROCKET_KEY || process.env.REACT_APP_LOG_ROCKET_KEY;
    if (isEmpty(user) || !logRocketKey) return;

    const domains = getLogRocketDomains();

    LogRocket.init(logRocketKey, {
      rootHostname: domains?.rootHostname,
      mergeIframes: true,
      childDomains: domains?.childDomains,
      parentDomain: domains?.rootHostname
    });

    LogRocket.identify(user?.id, {
      name: `${user?.personalInformation?.full_name}`,
      email: user?.email
    });
  }, [user]);
};

export const useUserData = ({ isLoggedIn, onResolveUser }) => {
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const isUserDataByEndpointEnabled = checkIfFeatureFlagEnabled(FeatureFlags.UserDataByEndpoint);
  const canFetchUserData = isLoggedIn && isUserDataByEndpointEnabled;

  const [status, setStatus] = useState(canFetchUserData ? UIStatus.Loading : UIStatus.Default);
  const isResolvingUser = canFetchUserData && [UIStatus.Default, UIStatus.Loading].includes(status);

  useEffect(() => {
    const controller = new AbortController();

    const fetchUserData = async () => {
      if (!canFetchUserData) {
        onResolveUser(getCurrentUser());
        setStatus(UIStatus.Default);
        return;
      }

      setStatus(UIStatus.Loading);

      try {
        const response = await API.get(Endpoints.UserData, {
          signal: controller.signal,
          timeout: USER_DATA_TIMEOUT_MS
        });
        if (controller.signal.aborted) return;

        const resolvedUser = response?.data ?? getCurrentUser();
        onResolveUser(resolvedUser);

        response?.data &&
          localStorage.setItem(PERSISTENCE_KEYS.USER_INFO, JSON.stringify(response.data));

        setStatus(UIStatus.Success);
      } catch (error) {
        if (controller.signal.aborted) return;

        onResolveUser(getCurrentUser());
        setStatus(UIStatus.Error);
      }
    };

    fetchUserData();

    return () => controller.abort();
  }, [canFetchUserData, onResolveUser]);

  useEffect(() => {
    if (!isLoggedIn) {
      localStorage.removeItem(PERSISTENCE_KEYS.USER_INFO);
    }
  }, [isLoggedIn]);

  return {
    isResolvingUser
  };
};
